import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  People,
  Euro,
  AccessTime,
  Person,
  Schedule,
  CheckCircle,
} from '@mui/icons-material';
// import { useAuth } from '../contexts/AuthContext';
import axios from '../config/axios';
import dayjs from 'dayjs';
import ModalResult from '../components/ModalResult';
import ModalConfirm from '../components/ModalConfirm';

const ReservationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // const { user } = useAuth();
  
  const [reservable, setReservable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalSeverity, setModalSeverity] = useState('info');
  const [modalTitle, setModalTitle] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  
  // Form state
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('');
  const [notes, setNotes] = useState('');
  
  // Available slots
  // const [availableSlots, setAvailableSlots] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Seleziona Data', 'Scegli Orario', 'Conferma Prenotazione'];

  useEffect(() => {
    fetchReservable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReservable = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/reservables/${id}`);
      setReservable(response.data);
      
      // Set default duration
      if (response.data.availableDurations.length > 0) {
        setSelectedDuration(response.data.availableDurations[0].toString());
      }
    } catch (err) {
      setError('Errore nel caricamento della risorsa');
      console.error('Error fetching reservable:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = async (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    
    if (date) {
      try {
        const response = await axios.get(`/api/reservables/${id}/available-slots`, {
          params: { date }
        });
        // setAvailableSlots(response.data);
        
        // Extract unique times from available slots
        const times = [...new Set(response.data.map(slot => slot.time))];
        setAvailableTimes(times);
      } catch (err) {
        console.error('Error fetching available slots:', err);
        // setAvailableSlots([]);
        setAvailableTimes([]);
      }
    }
  };

  // Filter available times based on selected duration
  const getFilteredAvailableTimes = () => {
    if (!selectedDuration || !availableTimes.length) {
      return availableTimes;
    }

    const durationMinutes = parseInt(selectedDuration);
    const dayOfWeek = new Date(selectedDate).getDay();
    const dayAvailability = reservable?.availability.find(avail => avail.dayOfWeek === dayOfWeek);
    
    if (!dayAvailability) {
      return [];
    }

    // Filter times that have enough space for the selected duration
    return availableTimes.filter(time => {
      const timeMinutes = time.split(':').reduce((hours, mins) => hours * 60 + parseInt(mins), 0);
      const endTimeMinutes = timeMinutes + durationMinutes;
      
      // Check if the end time is within the reservable's availability window
      const availabilityEndMinutes = dayAvailability.endTime.split(':').reduce((hours, mins) => hours * 60 + parseInt(mins), 0);
      
      return endTimeMinutes <= availabilityEndMinutes;
    });
  };

  const handleDurationChange = (duration) => {
    setSelectedDuration(duration);
    setSelectedTime('');
    
    // No need to refetch slots when duration changes
    // The available slots are the same regardless of duration
  };

  // const handleTimeChange = (time) => {
  //   setSelectedTime(time);
  // };

  const calculatePrice = () => {
    if (selectedDuration && reservable) {
      const durationHours = parseInt(selectedDuration) / 60;
      return (durationHours * reservable.costPerHour).toFixed(2);
    }
    return 0;
  };

  const handleSubmit = () => {
    if (!selectedDate || !selectedTime || !selectedDuration) {
      setError('Compila tutti i campi obbligatori');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    
    try {
      setSubmitting(true);
      setError(null);

      const reservationData = {
        reservableId: id,
        date: selectedDate,
        startTime: selectedTime,
        duration: parseInt(selectedDuration),
        notes: notes.trim() || undefined,
      };

      await axios.post('/api/reservations', reservationData);
      
      // Show success modal
      setModalTitle('Prenotazione Creata');
      setModalMessage('La tua prenotazione è stata creata con successo! Riceverai una conferma via email.');
      setModalSeverity('success');
      setModalOpen(true);
      
    } catch (err) {
      // Show error modal
      setModalTitle('Errore Prenotazione');
      setModalMessage(err.response?.data?.message || 'Si è verificato un errore durante la creazione della prenotazione. Riprova più tardi.');
      setModalSeverity('error');
      setModalOpen(true);
      console.error('Error creating reservation:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    // Navigate back to reservables after modal closes
    navigate('/my-reservations');
  };

  const getCategoryLabel = (category) => {
    const labels = {
      sala: 'Sala',
      istruttore: 'Istruttore',
      attrezzatura: 'Attrezzatura'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      sala: 'primary',
      istruttore: 'secondary',
      attrezzatura: 'success'
    };
    return colors[category] || 'default';
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    return days[dayOfWeek];
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !reservable) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/reservables')}
        >
          Torna alle prenotazioni
        </Button>
      </Container>
    );
  }

  return (
    <>
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/reservables')}
          sx={{ mb: 2 }}
        >
          Torna alle prenotazioni
        </Button>
        
        <Typography variant="h4" gutterBottom>
          Prenota: {reservable?.name}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip
            label={getCategoryLabel(reservable?.category)}
            color={getCategoryColor(reservable?.category)}
            size="small"
          />
          {reservable?.requiresApproval && (
            <Chip
              label="Richiede approvazione"
              color="warning"
              size="small"
              icon={<Schedule />}
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Reservable Info */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Dettagli Risorsa
              </Typography>
              
              {reservable?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {reservable.description}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOn sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {reservable?.location || 'Non specificata'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <People sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Capacità: {reservable?.capacity} persona{reservable?.capacity === 1 ? '' : 'e'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Euro sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {reservable?.costPerHour}€/ora
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Durate disponibili: {reservable?.availableDurations.map(d => formatDuration(d)).join(', ')}
                </Typography>
              </Box>

              {reservable?.teacher && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Person sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Istruttore: {reservable.teacher.name} {reservable.teacher.surname}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Disponibilità:
              </Typography>
              {reservable?.availability.map((avail, index) => (
                <Chip
                  key={index}
                  label={`${getDayName(avail.dayOfWeek)} ${avail.startTime}-${avail.endTime}`}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Reservation Form */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Crea Prenotazione
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: Date Selection */}
            {activeStep === 0 && (
              <Box>
                <TextField
                  fullWidth
                  label="Data"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setActiveStep(1);
                    handleDateChange(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: dayjs().format('YYYY-MM-DD'),
                    max: dayjs().add(30, 'days').format('YYYY-MM-DD')
                  }}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(1)}
                    disabled={!selectedDate}
                  >
                    Avanti
                  </Button>
                </Box>
              </Box>
            )}

            {/* Step 2: Time and Duration Selection */}
            {activeStep === 1 && (
              <Box>
                {selectedDate && (
                  <Box sx={{ mb: 2, p: 2, backgroundColor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
                    <Typography variant="body2">
                      <strong>Data selezionata:</strong> {getDayName(new Date(selectedDate).getDay())} {dayjs(selectedDate).format('DD-MMM-YY')}
                    </Typography>
                  </Box>
                )}
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Durata</InputLabel>
                  <Select
                    value={selectedDuration}
                    onChange={(e) => handleDurationChange(e.target.value)}
                    label="Durata"
                  >
                    {reservable?.availableDurations.map((duration) => (
                      <MenuItem key={duration} value={duration}>
                        {formatDuration(duration)} - {(duration / 60 * reservable.costPerHour).toFixed(2)}€
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedDate && selectedDuration ? (
                  (() => {
                    const filteredTimes = getFilteredAvailableTimes();
                    return filteredTimes.length > 0 ? (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Orario</InputLabel>
                        <Select
                          value={selectedTime}
                          onChange={(e) => {
                            setSelectedTime(e.target.value);
                            setActiveStep(2);
                          }}
                          label="Orario"
                        >
                          {filteredTimes.map((time) => (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Nessun orario disponibile per questa data e durata
                      </Alert>
                    );
                  })()
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Seleziona prima una data e una durata per vedere gli orari disponibili
                  </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(0)}
                  >
                    Indietro
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(2)}
                    disabled={!selectedTime}
                  >
                    Avanti
                  </Button>
                </Box>
              </Box>
            )}

            {/* Step 3: Confirmation */}
            {activeStep === 2 && (
              <Box>
                {selectedDate && (
                  <Box sx={{ mb: 2, p: 2, backgroundColor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
                    <Typography variant="body2">
                      <strong>Data selezionata:</strong> {getDayName(new Date(selectedDate).getDay())} {dayjs(selectedDate).format('DD-MMM-YY')}
                    </Typography>
                  </Box>
                )}
                
                <Typography variant="h6" gutterBottom>
                  Riepilogo Prenotazione
                </Typography>

                <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Data:</strong> {getDayName(new Date(selectedDate).getDay())} {dayjs(selectedDate).format('DD-MMM-YY')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Orario:</strong> {selectedTime} - {(() => {
                      const startMinutes = selectedTime.split(':').reduce((hours, mins) => hours * 60 + parseInt(mins), 0);
                      const endMinutes = startMinutes + parseInt(selectedDuration);
                      const endHours = Math.floor(endMinutes / 60);
                      const endMins = endMinutes % 60;
                      return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
                    })()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Durata:</strong> {formatDuration(parseInt(selectedDuration))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Prezzo:</strong> {calculatePrice()}€
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Note (opzionale)"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                 
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(1)}
                    fullWidth
                  >
                    Indietro
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
                    fullWidth
                  >
                    {submitting ? 'Creando...' : 'Conferma Prenotazione'}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Modal di conferma */}
      <ModalConfirm
        open={confirmOpen}
        title="Conferma Prenotazione"
        message={`Sei sicuro di voler creare questa prenotazione per ${reservable?.name} il ${getDayName(new Date(selectedDate).getDay())} ${dayjs(selectedDate).format('DD-MMM-YY')} alle ${selectedTime}?`}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelConfirm}
      />

      {/* Modal Result */}
      <ModalResult
        open={modalOpen}
        onClose={handleModalClose}
        message={modalMessage}
        severity={modalSeverity}
        title={modalTitle}
      />
    </>
  );
};

export default ReservationDetail;
